import { useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Building2, AlertCircle } from 'lucide-react'
import { getApiBase, getToken } from '../lib/utils'

interface CreateWorkspaceModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function CreateWorkspaceModal({ isOpen, onClose, onSuccess }: CreateWorkspaceModalProps) {
  const [workspaceName, setWorkspaceName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const token = getToken()
      if (!token) {
        setError('Vous devez être connecté pour créer un workspace')
        setLoading(false)
        return
      }

      const response = await fetch(`${getApiBase()}/workspaces`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: workspaceName,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Erreur lors de la création du workspace')
      }

      const workspace = await response.json()
      
      // Stocker le nouveau workspace comme workspace actuel
      localStorage.setItem('current_workspace_id', workspace.id.toString())
      
      // Callback de succès
      if (onSuccess) {
        onSuccess()
      }
      
      // Fermer le modal
      onClose()
      
      // Recharger la page pour appliquer le contexte du nouveau workspace
      window.location.reload()
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setWorkspaceName('')
      setError(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return createPortal(
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-zinc-100">Nouveau workspace</h2>
                <p className="text-sm text-zinc-500">Créez un espace de travail supplémentaire</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-400">Erreur</p>
                  <p className="text-sm text-red-300 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Workspace Name Input */}
            <div>
              <label htmlFor="workspace-name" className="block text-sm font-medium text-zinc-300 mb-2">
                Nom du workspace
              </label>
              <input
                id="workspace-name"
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="Ex: Mon projet, Client X, Équipe Marketing..."
                disabled={loading}
                required
                minLength={1}
                maxLength={255}
                className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-zinc-500 mt-2">
                Le nom peut être modifié ultérieurement dans les paramètres
              </p>
            </div>

            {/* Info Box */}
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-sm text-emerald-300">
                <strong>Plan Agency requis</strong> : Vous pouvez créer jusqu'à 10 workspaces avec le plan Agency.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-3 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || !workspaceName.trim()}
                className="flex-1 px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Création...' : 'Créer le workspace'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  )
}
